import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import API_BASE from "../../config";
import Select from "../../components/Select";
import SectionRoster from "../academics/SectionRoster";
import TeacherDetailModal from "../../components/TeacherDetailModal";
import SubjectDetailModal from "../../components/SubjectDetailModal";
import s from "./AcademicDirectoryPage.module.css";

/** Read whichever storage the LoginPage chose (sessionStorage by default,
 *  localStorage when "remember me" was ticked). Same fallback as the fetch
 *  interceptor in index.js so we agree on who's logged in. */
function readCurrentUser() {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/** Deterministic avatar color per name — mirrors PeoplePage so the same
 *  staff member gets the same tile color across the app. */
const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Read-only user view of the academic catalog. Browsable by School Year,
 *  Level (a TEACHING department), and Grade Level. Non-destructive — the
 *  admin CRUD for this data lives under /academics. The "Level" filter
 *  used to be a Faculty before the Faculty→Department merge. */
export default function AcademicDirectoryPage() {
  const [schoolYears,   setSchoolYears]   = useState([]);
  const [semesters,     setSemesters]     = useState([]);
  const [teachingDepts, setTeachingDepts] = useState([]); // "Levels" — TEACHING-tagged departments
  const [gradeLevels,   setGradeLevels]   = useState([]);
  const [sections,      setSections]      = useState([]);
  const [subjects,      setSubjects]      = useState([]);
  const [offerings,     setOfferings]     = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [rooms,         setRooms]         = useState([]); // Homerooms — surfaced in the section detail modal
  const [loading,       setLoading]       = useState(true);

  const [syId,    setSyId]    = useState("");  // "" means "active SY"
  const [levelId, setLevelId] = useState("");  // "" means all levels (TEACHING departments)
  const [glId,    setGlId]    = useState("");  // "" means all grades
  const [search,  setSearch]  = useState("");

  // Section detail modal — tracks which row was clicked. Closing nulls it.
  // Stored as id (not the section object) so a stale closure can't pin an
  // outdated record if the section ever changes during the modal lifetime.
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  // Teacher / Subject detail popups — independent of the section modal so
  // either one can open from the main list (no section modal involved) OR
  // from within an already-open SectionDetailModal (stacks on top of it,
  // same pattern the Students tab already uses for its own nested
  // StudentDetailModal). Only one of each open at a time.
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);

  // "Show only my sections" toggle — visible only for staff (teachers,
  // advisers). Hidden for admins / non-linked accounts since they have
  // no staff identity to filter against. Persists in state only — no
  // localStorage write, so a fresh tab starts at "All" by design.
  const [onlyMine, setOnlyMine] = useState(false);

  // Lazy roster cache. Fetches per-section students + enrollments only
  // when a modal opens for that section — fetching every roster upfront
  // would balloon network for a directory of hundreds of sections, and
  // 99% of opens are quick scans, not deep dives. Keyed by sectionId
  // so the second open of the same section is instant.
  const [rosterCache, setRosterCache] = useState({});
  // { [sectionId]: { loading: boolean, students: Student[], enrollments: Enrollment[], error?: string } }

  // Logged-in user — read once on mount. Used to highlight "your" sections
  // when a teacher (or other linked-staff user) browses the directory.
  // Falls back to no highlight for admins / unlinked accounts.
  const currentUser = useMemo(() => readCurrentUser(), []);
  const myStaffId = useMemo(() => {
    const t = currentUser?.linkedEntityType || currentUser?.linkedEntity?.type;
    if (t !== "STAFF") return null;
    return currentUser?.linkedEntity?.id ?? currentUser?.linkedEntityId ?? null;
  }, [currentUser]);
  // Teachers get the "Encode →" deep-links per subject row inside the
  // modal. Admins / non-teachers do not — the link only makes sense for
  // the offering's owner (the encoder UI itself also gates writes via
  // requireTeacherOrAdmin on the backend, so this is a UX hint).
  const isTeacher = currentUser?.role === "Teacher";

  // A Student login's own section — already embedded in the login
  // payload (linkedEntity.currentSectionId for a STUDENT-linked
  // account), no extra fetch needed. Drives both the auto-scoped
  // default view below and the "Your Section" highlight on the card.
  // Deliberately STUDENT-only, not Parent: a parent can have multiple
  // children across different grade levels, so there's no single
  // section to default to without first picking which child.
  const myStudentSectionId = useMemo(() => {
    const t = currentUser?.linkedEntityType || currentUser?.linkedEntity?.type;
    if (t !== "STUDENT") return null;
    return currentUser?.linkedEntity?.currentSectionId ?? null;
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/departments?staffType=TEACHING`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
      // Rooms power the homeroom row in the detail modal. Failing softly to []
      // keeps the directory usable even if /api/rooms is unavailable.
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
    ]).then(([sy, sem, depts, gl, sec, subj, off, emp, rms]) => {
      if (cancelled) return;
      setSchoolYears(sy); setSemesters(sem); setTeachingDepts(depts);
      setGradeLevels(gl); setSections(sec); setSubjects(subj);
      setOfferings(off);  setEmployees(emp); setRooms(rms || []);

      // Default to the active school year if one exists; otherwise the first.
      const active = sy.find(y => y.active) || sy[0];
      if (active) setSyId(String(active.id));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Fast lookups — O(1) by id instead of repeated Array.find.
  const levelById      = useMemo(() => Object.fromEntries(teachingDepts.map(d => [d.id, d])), [teachingDepts]);
  const gradeLevelById = useMemo(() => Object.fromEntries(gradeLevels.map(g => [g.id, g])), [gradeLevels]);
  const subjectById    = useMemo(() => Object.fromEntries(subjects.map(s => [s.id, s])), [subjects]);
  const employeeById   = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
  const syById         = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const semesterById   = useMemo(() => Object.fromEntries(semesters.map(s => [s.id, s])), [semesters]);
  const roomById       = useMemo(() => Object.fromEntries(rooms.map(r => [r.id, r])), [rooms]);

  // Modal interactions — ESC to close, body scroll lock so the page doesn't
  // jitter beneath the overlay. Both effects no-op when the modal is closed,
  // so there's no global listener / style mutation while idle.
  useEffect(() => {
    if (selectedSectionId == null) return;
    const onKey = (e) => { if (e.key === "Escape") setSelectedSectionId(null); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedSectionId]);

  // Lazy roster fetch — only when a modal opens for a section we haven't
  // loaded yet. Two parallel requests scoped server-side via the
  // sectionId filter, so we never pull the whole-school students /
  // enrollments dataset into this read-only page.
  useEffect(() => {
    if (selectedSectionId == null) return undefined;
    if (rosterCache[selectedSectionId]) return undefined; // cached
    let cancelled = false;
    setRosterCache(prev => ({
      ...prev,
      [selectedSectionId]: { loading: true, students: [], enrollments: [] },
    }));
    Promise.all([
      fetch(`${API_BASE}/api/students?sectionId=${selectedSectionId}`)
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/enrollments?sectionId=${selectedSectionId}`)
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([stu, enr]) => {
      if (cancelled) return;
      setRosterCache(prev => ({
        ...prev,
        [selectedSectionId]: {
          loading: false,
          students:    Array.isArray(stu) ? stu : [],
          enrollments: Array.isArray(enr) ? enr : [],
        },
      }));
    }).catch(() => {
      if (cancelled) return;
      setRosterCache(prev => ({
        ...prev,
        [selectedSectionId]: { loading: false, students: [], enrollments: [], error: "Roster unavailable" },
      }));
    });
    return () => { cancelled = true; };
  }, [selectedSectionId, rosterCache]);

  // When a Level is picked, grade-level options shrink to just its children.
  // Grade levels reference their parent via the new departmentId field
  // (was facultyId before the Faculty→Department merge).
  const visibleGradeLevels = useMemo(
    () => gradeLevels.filter(g => !levelId || String(g.departmentId) === String(levelId)),
    [gradeLevels, levelId]
  );

  // Reset grade-level if the level switched away from its parent dept.
  useEffect(() => {
    if (!glId) return;
    const gl = gradeLevelById[glId];
    if (levelId && gl && String(gl.departmentId) !== String(levelId)) setGlId("");
  }, [levelId, glId, gradeLevelById]);

  // Semesters that belong to the chosen SY — used to filter offerings.
  const semesterIdsInSY = useMemo(() => {
    if (!syId) return new Set(semesters.map(s => s.id));
    return new Set(semesters.filter(s => String(s.schoolYearId) === String(syId)).map(s => s.id));
  }, [semesters, syId]);

  // Group offerings by their section so we can render section cards.
  const offeringsBySection = useMemo(() => {
    const map = new Map();
    for (const o of offerings) {
      if (!semesterIdsInSY.has(o.semesterId)) continue;
      if (!map.has(o.sectionId)) map.set(o.sectionId, []);
      map.get(o.sectionId).push(o);
    }
    return map;
  }, [offerings, semesterIdsInSY]);

  // Set of section ids the logged-in staff member is associated with — they
  // either teach an offering in it OR are its form adviser. Used to render
  // a subtle "Yours" accent on those cards. Empty for non-staff users so
  // admins see the directory uniformly.
  const mySectionIds = useMemo(() => {
    if (!myStaffId) return new Set();
    const set = new Set();
    for (const o of offerings) {
      if (String(o.teacherStaffId) === String(myStaffId) && o.sectionId != null) {
        set.add(o.sectionId);
      }
    }
    for (const sec of sections) {
      if (sec.adviserStaffId != null && String(sec.adviserStaffId) === String(myStaffId)) {
        set.add(sec.id);
      }
    }
    return set;
  }, [myStaffId, offerings, sections]);

  // Section card list — filtered by SY (via section.schoolYearId), level,
  // grade-level, the search box, and (for staff users) the "Show only my
  // sections" toggle.
  const visibleSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sections
      .filter(sec => !syId || String(sec.schoolYearId) === String(syId))
      .filter(sec => !onlyMine || mySectionIds.has(sec.id))
      .filter(sec => {
        if (!levelId) return true;
        const gl = gradeLevelById[sec.gradeLevelId];
        return gl && String(gl.departmentId) === String(levelId);
      })
      .filter(sec => !glId || String(sec.gradeLevelId) === String(glId))
      .filter(sec => {
        if (!q) return true;
        // Match against section name, adviser, any subject code/title, any teacher.
        if ((sec.name || "").toLowerCase().includes(q)) return true;
        const adviser = employeeById[sec.adviserStaffId];
        if (adviser?.name?.toLowerCase().includes(q)) return true;
        const sectionOfferings = offeringsBySection.get(sec.id) || [];
        return sectionOfferings.some(o => {
          const subj = subjectById[o.subjectId];
          const teacher = employeeById[o.teacherStaffId];
          return (subj?.code || "").toLowerCase().includes(q)
              || (subj?.title || "").toLowerCase().includes(q)
              || (teacher?.name || "").toLowerCase().includes(q);
        });
      })
      .sort((a, b) => {
        const ga = gradeLevelById[a.gradeLevelId];
        const gb = gradeLevelById[b.gradeLevelId];
        const oa = ga?.ordering ?? 999;
        const ob = gb?.ordering ?? 999;
        if (oa !== ob) return oa - ob;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [sections, syId, levelId, glId, search, onlyMine, mySectionIds,
      gradeLevelById, employeeById, subjectById, offeringsBySection]);

  // Summary for the result count pill.
  const totalSubjectsShown = useMemo(
    () => visibleSections.reduce((n, sec) => n + (offeringsBySection.get(sec.id)?.length || 0), 0),
    [visibleSections, offeringsBySection]
  );
  const totalTeachersShown = useMemo(() => {
    const ids = new Set();
    for (const sec of visibleSections) {
      if (sec.adviserStaffId) ids.add(sec.adviserStaffId);
      for (const o of offeringsBySection.get(sec.id) || []) {
        if (o.teacherStaffId) ids.add(o.teacherStaffId);
      }
    }
    return ids.size;
  }, [visibleSections, offeringsBySection]);

  const selectedSy = syId ? syById[syId] : null;

  // Entrance-animation gate. Cards fade-up only on the FIRST render after
  // data loads — afterwards we drop the class so search-as-you-type doesn't
  // replay the stagger on every keystroke. Single one-shot timer, cleaned
  // up on unmount.
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  useEffect(() => {
    if (loading || hasAnimatedIn) return;
    const t = setTimeout(() => setHasAnimatedIn(true), 900);
    return () => clearTimeout(t);
  }, [loading, hasAnimatedIn]);

  // Sets School Year / Level / Grade Level to match the student's own
  // section, and clears Search so a leftover query doesn't hide their
  // own card. Shared by the one-time auto-scope effect below AND the
  // "My Section" button, so there's exactly one place that defines what
  // "scoped to my section" means — the button can never drift out of
  // sync with what happens automatically on load. Returns the resolved
  // section (or null) so a caller can act on it further, e.g. the
  // button also opens the section's detail popup.
  function scopeToMySection() {
    if (myStudentSectionId == null) return null;
    const sec = sections.find(x => x.id === myStudentSectionId);
    if (!sec) return null;
    const gl = gradeLevelById[sec.gradeLevelId];
    if (sec.schoolYearId != null) setSyId(String(sec.schoolYearId));
    if (gl?.departmentId != null) setLevelId(String(gl.departmentId));
    if (sec.gradeLevelId != null) setGlId(String(sec.gradeLevelId));
    setSearch("");
    return sec;
  }

  // Auto-scope a Student login to their own section the first time data
  // finishes loading, so they land there instead of the whole school's
  // 25+ sections. Runs once (hasAutoScoped gate) — after that the
  // student is free to switch tabs/filters to browse elsewhere without
  // this fighting them back; the "My Section" button is how they get
  // back on demand. A no-op for every other role (myStudentSectionId is
  // null for Teacher/Admin/Parent/unlinked accounts).
  const [hasAutoScoped, setHasAutoScoped] = useState(false);
  useEffect(() => {
    if (hasAutoScoped || loading) return;
    setHasAutoScoped(true); // mark done regardless of outcome below — one shot only
    scopeToMySection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasAutoScoped, myStudentSectionId, sections, gradeLevelById]);

  // Per-Level section counts — drives the "PRE-SCHOOL · 3 sections" group
  // dividers between cards. Only used when no Level filter is active, so a
  // narrowed view doesn't show a single redundant divider.
  const levelGroupCounts = useMemo(() => {
    const map = new Map();
    for (const sec of visibleSections) {
      const gl  = gradeLevelById[sec.gradeLevelId];
      const lvl = gl ? levelById[gl.departmentId] : null;
      const key = lvl?.id ?? "__none__";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [visibleSections, gradeLevelById, levelById]);

  // Per-level counts for the Level TABS themselves — deliberately scoped
  // by School Year only (same convention the "All sections / My sections"
  // scope toggle already uses for ITS counts), NOT by the currently
  // selected level/grade/search. If this used visibleSections instead,
  // every non-active tab would show a stale/zero count the moment you
  // switched away from it, since visibleSections is already narrowed to
  // whichever level is picked.
  const sectionCountByLevel = useMemo(() => {
    const map = new Map();
    let total = 0;
    for (const sec of sections) {
      if (syId && String(sec.schoolYearId) !== String(syId)) continue;
      total++;
      const gl = gradeLevelById[sec.gradeLevelId];
      if (gl?.departmentId != null) {
        map.set(gl.departmentId, (map.get(gl.departmentId) || 0) + 1);
      }
    }
    map.set("__all__", total);
    return map;
  }, [sections, syId, gradeLevelById]);

  // Some school setups have accumulated duplicate/legacy department rows
  // that were all tagged TEACHING at some point (e.g. both "Elementary"
  // and "Elementary Department" existing as separate records after a
  // rename) — every real section lives under only ONE of each pair, so
  // the other always shows a 0 count and is pure clutter in the tab row.
  // Hide a department tab once it has no sections for the selected
  // school year, UNLESS it's the one currently active — we never want
  // switching School Year to make the tab you're standing on vanish out
  // from under you. This is purely a display filter: nothing here
  // touches the department records themselves, so merging/archiving the
  // actual duplicates (if that's wanted) is still a separate decision.
  // Departments have no ordering field of their own, so "school
  // progression" order (Pre-School → Elementary → Junior High → Senior
  // High) is derived from the grade levels underneath each one — same
  // `ordering` field the group dividers above already sort sections by.
  // Using the LOWEST ordering among a department's own grade levels
  // means e.g. "Pre-School" sorts first because it contains Nursery
  // (ordering 0), not because of anything about the department record
  // itself. A department with no grade levels yet sorts last rather
  // than crashing the comparison.
  const minGradeOrderingByDept = useMemo(() => {
    const map = new Map();
    for (const gl of gradeLevels) {
      if (gl.departmentId == null) continue;
      const cur = map.get(gl.departmentId);
      const ord = gl.ordering ?? 0;
      if (cur == null || ord < cur) map.set(gl.departmentId, ord);
    }
    return map;
  }, [gradeLevels]);

  const visibleLevelTabs = useMemo(() => {
    return teachingDepts
      .filter(d => (sectionCountByLevel.get(d.id) || 0) > 0 || String(d.id) === String(levelId))
      .slice()
      .sort((a, b) => {
        const oa = minGradeOrderingByDept.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const ob = minGradeOrderingByDept.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return oa !== ob ? oa - ob : (a.name || "").localeCompare(b.name || "");
      });
  }, [teachingDepts, sectionCountByLevel, levelId, minGradeOrderingByDept]);

  return (
    <div className={s.wrap}>
      <div className={s.header}>
        <div className={s.eyebrow}>ACADEMIC DIRECTORY</div>
        <h1 className={s.title}>Classes, subjects, and teachers — all in one place</h1>
        <p className={s.sub}>
          Browse every section in the school. Pick a school year, drill into a level,
          then a grade — the directory updates live. Search across section names,
          subjects, and teachers.
        </p>
      </div>

      {/* Filter bar */}
      <div className={s.filterCard}>
        <div className={s.levelTabsRow}>
          <LevelTabs
            levels={visibleLevelTabs}
            activeId={levelId}
            onChange={setLevelId}
            countsByLevel={sectionCountByLevel}
          />
          {/* Student-only shortcut — re-applies the same School Year /
              Level / Grade scoping the page auto-does on first load, so
              a student who's wandered off to browse other grades has a
              one-click way back, and jumps straight into their own
              section's detail popup as a bonus rather than just
              filtering down to a list of one. */}
          {myStudentSectionId != null && (
            <button
              type="button"
              className={s.mySectionBtn}
              onClick={() => {
                const sec = scopeToMySection();
                if (sec) setSelectedSectionId(sec.id);
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                   strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2l2.9 6.3L22 9l-5 5 1.2 7L12 17.8 5.8 21 7 14 2 9l7.1-.7L12 2z" />
              </svg>
              My Section
            </button>
          )}
        </div>

        <div className={s.filterGrid}>
          <div className={s.filterField}>
            <span>School Year</span>
            <Select
              value={syId == null ? "" : String(syId)}
              onChange={setSyId}
              options={[
                { value: "", label: "All school years" },
                ...schoolYears.map(y => ({
                  value: String(y.id),
                  label: `${y.label}${y.active ? " · Active" : ""}`,
                })),
              ]}
              ariaLabel="School year"
            />
          </div>

          <div className={s.filterField}>
            <span>Grade Level</span>
            <Select
              value={glId == null ? "" : String(glId)}
              onChange={setGlId}
              options={[
                { value: "",
                  label: visibleGradeLevels.length === 0 ? "No grades for this level" : "All grades" },
                ...visibleGradeLevels.map(g => ({ value: String(g.id), label: g.name })),
              ]}
              disabled={visibleGradeLevels.length === 0}
              ariaLabel="Grade level"
            />
          </div>

          <label className={`${s.filterField} ${s.searchField}`}>
            <span>Search</span>
            <div className={s.searchWrap}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                   className={s.searchIcon} aria-hidden>
                <circle cx="9" cy="9" r="6" />
                <path d="M15 15l-3.5-3.5" />
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Teacher, subject, or section…"
              />
              {search && (
                <button className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear search">
                  ✕
                </button>
              )}
            </div>
          </label>
        </div>

        {/* Scope toggle — segmented control with "All sections" vs "My
            sections". Only rendered for staff (teachers / advisers); admins
            and unlinked users don't have a staff identity to scope by, so
            the control would be a no-op for them. Falls inline with the
            filter card so the page chrome stays unified. */}
        {myStaffId != null && (
          <div className={s.scopeRow}>
            <div className={s.scopeLabel}>Show</div>
            <div className={s.scopeTabs} role="tablist" aria-label="Filter scope">
              <button
                type="button"
                role="tab"
                aria-selected={!onlyMine}
                className={`${s.scopeTab} ${!onlyMine ? s.scopeTabActive : ""}`}
                onClick={() => setOnlyMine(false)}>
                All sections
                <span className={s.scopeTabCount}>{sections.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={onlyMine}
                className={`${s.scopeTab} ${onlyMine ? s.scopeTabActive : ""}`}
                onClick={() => setOnlyMine(true)}>
                My sections
                <span className={s.scopeTabCount}>{mySectionIds.size}</span>
              </button>
            </div>
            {onlyMine && mySectionIds.size === 0 && (
              <span className={s.scopeHint}>
                You aren't assigned to any sections this term — nothing to show.
              </span>
            )}
          </div>
        )}

        <div className={s.summary}>
          {loading ? (
            <span className={s.summaryPill}>Loading…</span>
          ) : (
            <>
              <span className={s.summaryPill}>
                {visibleSections.length} section{visibleSections.length === 1 ? "" : "s"}
              </span>
              <span className={s.summaryPill}>
                {totalSubjectsShown} subject{totalSubjectsShown === 1 ? "" : "s"}
              </span>
              <span className={s.summaryPill}>
                {totalTeachersShown} teacher{totalTeachersShown === 1 ? "" : "s"}
              </span>
              {selectedSy && (
                <span className={s.summaryActiveSy}>{selectedSy.label}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Section list */}
      {loading ? (
        <div className={s.sectionList} aria-busy="true" aria-label="Loading directory">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} style={{ "--i": i }} />
          ))}
        </div>
      ) : visibleSections.length === 0 ? (
        <div className={s.emptyCard}>
          <div className={s.emptyTitle}>Nothing matches the current filters.</div>
          <div className={s.emptyHint}>
            Try a different school year or clear the search box.
          </div>
        </div>
      ) : (
        <div className={s.sectionList}>
          {(() => {
            // Render-pass: interleave Level group dividers between cards
            // when no Level filter is active, so the eye can scan by
            // Pre-School / Grade School / etc. We pre-walk visibleSections
            // (already sorted by gradeLevel.ordering) and emit a divider
            // each time the Level id changes. Stagger delay is bound to
            // the card index only — capped to 16 so a 100-section list
            // doesn't queue a 4-second waterfall.
            const items = [];
            let prevLevelId = "__init__";
            visibleSections.forEach((sec, idx) => {
              const gl      = gradeLevelById[sec.gradeLevelId];
              const level   = gl ? levelById[gl.departmentId] : null;
              const adviser = employeeById[sec.adviserStaffId];
              const sy      = syById[sec.schoolYearId];
              const rows    = (offeringsBySection.get(sec.id) || [])
                .map(o => ({
                  offering: o,
                  subject:  subjectById[o.subjectId],
                  teacher:  employeeById[o.teacherStaffId],
                  semester: semesterById[o.semesterId],
                }))
                .sort((a, b) =>
                  (a.subject?.code || "").localeCompare(b.subject?.code || "")
                );

              const isMine  = mySectionIds.has(sec.id);
              const isMyStudentSection = myStudentSectionId != null && sec.id === myStudentSectionId;
              const empty   = rows.length === 0;
              const lvlKey  = level?.id ?? "__none__";

              if (!levelId && lvlKey !== prevLevelId) {
                const count = levelGroupCounts.get(lvlKey) || 0;
                items.push(
                  <div
                    key={`gd-${lvlKey}`}
                    className={`${s.groupDivider} ${!hasAnimatedIn ? s.groupDividerAnimate : ""}`}>
                    <div className={s.groupDividerLabel}>
                      <span className={s.groupDividerName}>{level?.name || "Other"}</span>
                      <span className={s.groupDividerCount}>
                        {count} section{count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className={s.groupDividerRule} />
                  </div>
                );
                prevLevelId = lvlKey;
              }

              items.push(
                <section
                  key={sec.id}
                  style={!hasAnimatedIn ? { "--i": Math.min(idx, 16) } : undefined}
                  className={`${s.sectionCard} ${isMine || isMyStudentSection ? s.sectionCardYours : ""} ${empty ? s.sectionCardCompact : ""} ${!hasAnimatedIn ? s.sectionCardAnimate : ""}`}>
                <header className={`${s.sectionHead} ${empty ? s.sectionHeadSolo : ""}`}>
                  <div className={s.sectionHeadMain}>
                    <h2 className={s.sectionName}>
                      <button
                        type="button"
                        className={s.sectionNameBtn}
                        onClick={() => setSelectedSectionId(sec.id)}
                        aria-haspopup="dialog"
                        aria-label={`Open details for ${sec.name}`}>
                        {sec.name}
                      </button>
                    </h2>
                    <div className={s.badgeRow}>
                      {isMine && <span className={s.badgeYours}>Yours</span>}
                      {isMyStudentSection && <span className={s.badgeYours}>Your Section</span>}
                      {gl    && <span className={s.badge}>{gl.name}</span>}
                      {level && <span className={s.badgeMuted}>{level.name}</span>}
                      {sy    && <span className={s.badgeMuted}>SY {sy.label}</span>}
                      {empty && <span className={s.badgeAwaiting}>Awaiting subjects</span>}
                    </div>
                  </div>

                  {adviser && (
                    <div className={s.adviser}>
                      <div className={s.adviserLabel}>Adviser</div>
                      <button
                        type="button"
                        className={`${s.plainBtn} ${s.adviserChip}`}
                        onClick={() => setSelectedTeacherId(adviser.id)}
                        aria-haspopup="dialog"
                        aria-label={`View teacher details for ${adviser.name}`}>
                        {adviser.photoUrl
                          ? <img src={adviser.photoUrl} alt="" className={s.adviserAvatarImg} />
                          : <div className={s.adviserAvatar} style={{ background: avatarColor(adviser.name) }}>
                              {initials(adviser.name)}
                            </div>
                        }
                        <div>
                          <div className={s.adviserName}>{adviser.name}</div>
                          {adviser.role && <div className={s.adviserRole}>{adviser.role}</div>}
                        </div>
                      </button>
                    </div>
                  )}
                </header>

                {!empty && (
                  <ul className={s.subjectList}>
                    {rows.map(({ offering, subject, teacher, semester }) => (
                      <li key={offering.id} className={s.subjectRow}>
                        <div className={s.subjectCodePill}>{subject?.code || "—"}</div>
                        <button
                          type="button"
                          className={`${s.plainBtn} ${s.subjectMain}`}
                          onClick={() => subject && setSelectedSubjectId(subject.id)}
                          disabled={!subject}
                          aria-haspopup="dialog"
                          aria-label={subject ? `View subject details for ${subject.title}` : undefined}>
                          <div className={s.subjectTitle}>{subject?.title || "(Untitled subject)"}</div>
                          <div className={s.subjectMeta}>
                            {subject?.units != null && <span>{subject.units} units</span>}
                            {offering.schedule && <span>{offering.schedule}</span>}
                            {offering.room     && <span>Room {offering.room}</span>}
                            {semester          && <span>{semester.label}</span>}
                          </div>
                        </button>
                        {teacher ? (
                          <button
                            type="button"
                            className={`${s.plainBtn} ${s.subjectTeacher}`}
                            onClick={() => setSelectedTeacherId(teacher.id)}
                            aria-haspopup="dialog"
                            aria-label={`View teacher details for ${teacher.name}`}>
                            {teacher.photoUrl
                              ? <img src={teacher.photoUrl} alt="" className={s.teacherAvatarImg} />
                              : <div className={s.teacherAvatar} style={{ background: avatarColor(teacher.name) }}>
                                  {initials(teacher.name)}
                                </div>
                            }
                            <div className={s.teacherName}>{teacher.name}</div>
                          </button>
                        ) : (
                          <div className={s.subjectTeacher}>
                            <div className={s.teacherEmpty}>No teacher assigned</div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              );
            });
            return items;
          })()}
        </div>
      )}

      {/* Section detail modal — portaled to <body> so it sits above page
          scroll and any sticky chrome. Resolves the selected id against
          live state each render so a stale closure can't pin outdated
          adviser / offering info. */}
      {selectedSectionId != null && createPortal(
        (() => {
          const sec = sections.find(x => x.id === selectedSectionId);
          if (!sec) return null;
          const gl       = gradeLevelById[sec.gradeLevelId];
          const level    = gl ? levelById[gl.departmentId] : null;
          const adviser  = employeeById[sec.adviserStaffId];
          const sy       = syById[sec.schoolYearId];
          const homeroom = sec.homeroomId != null ? roomById[sec.homeroomId] : null;
          const rows = (offeringsBySection.get(sec.id) || [])
            .map(o => ({
              offering: o,
              subject:  subjectById[o.subjectId],
              teacher:  employeeById[o.teacherStaffId],
              semester: semesterById[o.semesterId],
            }))
            .sort((a, b) =>
              (a.subject?.code || "").localeCompare(b.subject?.code || "")
            );
          const isMine = mySectionIds.has(sec.id);
          const isMyStudentSection = myStudentSectionId != null && sec.id === myStudentSectionId;
          const semester = rows[0]?.semester || semesters.find(sm => semesterIdsInSY.has(sm.id)) || null;
          const roster   = rosterCache[sec.id] || { loading: true, students: [], enrollments: [] };
          return (
            <SectionDetailModal
              section={sec}
              gradeLevel={gl}
              level={level}
              adviser={adviser}
              schoolYear={sy}
              semester={semester}
              homeroom={homeroom}
              offerings={rows}
              isMine={isMine}
              isMyStudentSection={isMyStudentSection}
              isTeacher={isTeacher}
              myStaffId={myStaffId}
              roster={roster}
              // Context arrays forwarded to the embedded SectionRoster so
              // its inner student-detail modal can resolve current term,
              // homeroom, enrolment history without re-fetching anything.
              allSections={sections}
              allGradeLevels={gradeLevels}
              allSchoolYears={schoolYears}
              allSemesters={semesters}
              allRooms={rooms}
              onTeacherClick={setSelectedTeacherId}
              onSubjectClick={setSelectedSubjectId}
              onClose={() => setSelectedSectionId(null)}
            />
          );
        })(),
        document.body
      )}

      {/* Teacher / Subject detail popups — rendered at the page level (not
          nested inside the section modal's JSX tree) so they work whether
          triggered from the main list OR from within an open section
          modal; in the latter case they portal to the same <body> target
          and their higher z-index (1090 vs the section modal's 1000)
          naturally stacks them on top, same as the Students tab's own
          nested StudentDetailModal already does. */}
      <TeacherDetailModal
        open={selectedTeacherId != null}
        teacher={selectedTeacherId != null ? employeeById[selectedTeacherId] : null}
        offerings={offerings}
        subjects={subjects}
        sections={sections}
        gradeLevels={gradeLevels}
        semesters={semesters}
        activeSyId={syId || null}
        onClose={() => setSelectedTeacherId(null)}
      />
      <SubjectDetailModal
        open={selectedSubjectId != null}
        subject={selectedSubjectId != null ? subjectById[selectedSubjectId] : null}
        offerings={offerings}
        teachers={employees}
        sections={sections}
        gradeLevels={gradeLevels}
        semesters={semesters}
        activeSyId={syId || null}
        onClose={() => setSelectedSubjectId(null)}
      />
    </div>
  );
}

/** Level tabs — the page's primary navigation ("All" + one tab per
 *  teaching department), replacing what used to be a plain dropdown.
 *  A sliding pill indicator (absolutely positioned, animated via
 *  transform/width) tracks the active tab instead of each tab painting
 *  its own background, so switching tabs reads as one continuous motion
 *  rather than a hard cut. Purely presentational + a single onChange
 *  callback — no fetches, no state beyond the indicator's own geometry,
 *  so there's nothing here that can drift out of sync with the actual
 *  filter it's driving (`levelId` stays the single source of truth,
 *  owned by the parent). */
function LevelTabs({ levels, activeId, onChange, countsByLevel }) {
  const containerRef = useRef(null);
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ top: 0, left: 0, width: 0, height: 0, ready: false });

  const activeKey = activeId == null || activeId === "" ? "all" : String(activeId);

  // Measures the active tab's full box (top/left/width/height), not just
  // left/width — with enough tabs to wrap onto a second row, a height
  // driven by CSS percentage of the (now two-row-tall) container used to
  // stretch the indicator down over the row below instead of hugging
  // just the active tab. offsetTop/offsetLeft are relative to the
  // nearest positioned ancestor, which is this container
  // (`.levelTabs` is `position: relative`), so no manual subtraction
  // against a bounding-rect is needed.
  const measure = () => {
    const el = tabRefs.current[activeKey];
    const container = containerRef.current;
    if (!el || !container) return;
    setIndicator({
      top: el.offsetTop,
      left: el.offsetLeft,
      width: el.offsetWidth,
      height: el.offsetHeight,
      ready: true,
    });
  };

  // Re-measure synchronously before paint whenever the active tab or the
  // tab SET changes (e.g. teaching departments finish loading) — avoids a
  // one-frame flash of the indicator in its previous position.
  useLayoutEffect(measure, [activeKey, levels, countsByLevel]);

  // Tab labels/counts can reflow the row's width (e.g. a narrower viewport
  // wrapping it, or a late data load changing a count's digit count) —
  // re-measure on resize so the indicator doesn't drift out from under
  // its tab. Single passive listener, cleaned up on unmount.
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  return (
    <div className={s.levelTabs} role="tablist" aria-label="School level" ref={containerRef}>
      <div
        className={s.levelTabIndicator}
        style={indicator.ready
          ? {
              transform: `translate(${indicator.left}px, ${indicator.top}px)`,
              width: `${indicator.width}px`,
              height: `${indicator.height}px`,
              opacity: 1,
            }
          : { opacity: 0 }}
        aria-hidden="true"
      />
      <button
        type="button"
        role="tab"
        aria-selected={activeKey === "all"}
        ref={el => { tabRefs.current.all = el; }}
        className={`${s.levelTab} ${activeKey === "all" ? s.levelTabActive : ""}`}
        onClick={() => onChange("")}>
        <span>All</span>
        <span className={s.levelTabCount}>{countsByLevel.get("__all__") || 0}</span>
      </button>
      {levels.map(lvl => {
        const key = String(lvl.id);
        const active = activeKey === key;
        return (
          <button
            key={lvl.id}
            type="button"
            role="tab"
            aria-selected={active}
            ref={el => { tabRefs.current[key] = el; }}
            className={`${s.levelTab} ${active ? s.levelTabActive : ""}`}
            onClick={() => onChange(key)}>
            <span>{lvl.name}</span>
            <span className={s.levelTabCount}>{countsByLevel.get(lvl.id) || 0}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Section detail modal — a portaled, focus-aware overlay surfacing every
 *  piece of info the directory already knows about a section. Read-only:
 *  no fetches inside this component, no mutations, no permission-
 *  elevation. The roster + per-subject "Encode →" deep-link are
 *  conditional UI hints — the encoder route gates writes server-side via
 *  requireTeacherOrAdmin, so a non-owner clicking the link still can't
 *  save. Closes on ESC, overlay-click, or the explicit Close button. */
function SectionDetailModal({
  section, gradeLevel, level, adviser, schoolYear, semester, homeroom,
  offerings, isMine, isMyStudentSection, isTeacher, myStaffId, roster,
  allSections, allGradeLevels, allSchoolYears, allSemesters, allRooms,
  onTeacherClick, onSubjectClick,
  onClose,
}) {
  // The teacher's own offerings inside this section — drives the
  // Grading tab visibility and the footer hint. Computed once per render.
  const myOfferings = isTeacher && myStaffId != null
    ? offerings.filter(o => String(o.offering?.teacherStaffId) === String(myStaffId))
    : [];

  // Tab navigation — modern peer-level destinations inside the modal
  // (Overview / Subjects / Students / Grading). Default to Overview so
  // a quick scan lands on the section identity card. The Grading tab
  // is rendered only for teachers who own at least one offering here.
  const [activeTab, setActiveTab] = useState("overview");
  const hasGradingTab = myOfferings.length > 0;

  // Roster count for the Students tab badge — null while loading so the
  // pill doesn't flash a "0" before the fetch lands.
  const rosterCount = roster && !roster.loading
    ? (roster.enrollments || []).filter(e =>
        String(e.sectionId) === String(section.id) &&
        String(e.status || "ENROLLED").toUpperCase() === "ENROLLED"
      ).length
    : null;

  return (
    <div
      className={s.detailOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="acd-detail-title">
      <div className={s.detailModal} onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className={s.detailClose}
          onClick={onClose}
          aria-label="Close section details">
          ✕
        </button>

        <header className={s.detailHero}>
          <div
            className={s.detailAvatar}
            style={{ background: avatarColor(section.name) }}
            aria-hidden="true">
            {initials(section.name)}
          </div>
          <div className={s.detailHeroMain}>
            <div className={s.detailEyebrow}>SECTION</div>
            <h2 id="acd-detail-title" className={s.detailTitle}>
              {section.name}
              {isMine && <span className={s.badgeYours} style={{ marginLeft: 10 }}>Yours</span>}
              {isMyStudentSection && <span className={s.badgeYours} style={{ marginLeft: 10 }}>Your Section</span>}
            </h2>
            <div className={s.detailMetaRow}>
              {gradeLevel && <span className={s.badge}>{gradeLevel.name}</span>}
              {level      && <span className={s.badgeMuted}>{level.name}</span>}
              {schoolYear && (
                <span className={s.badgeMuted}>
                  SY {schoolYear.label}{schoolYear.active ? " · Active" : ""}
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Tab strip — modern peer-level navigation. Sticks to the
            modal header so switching tabs doesn't move the focus away
            from the chrome. Pills with a count badge per tab. */}
        <div className={s.tabStrip} role="tablist" aria-label="Section detail tabs">
          <TabBtn id="overview" active={activeTab === "overview"} onClick={setActiveTab}>
            Overview
          </TabBtn>
          <TabBtn id="subjects" active={activeTab === "subjects"} onClick={setActiveTab}
                  count={offerings.length || null}>
            Subjects
          </TabBtn>
          <TabBtn id="students" active={activeTab === "students"} onClick={setActiveTab}
                  count={rosterCount}>
            Students
          </TabBtn>
          {hasGradingTab && (
            <TabBtn
              id="grading"
              active={activeTab === "grading"}
              onClick={setActiveTab}
              count={myOfferings.length}
              accent>
              Grading
            </TabBtn>
          )}
        </div>

        <div className={s.detailBody}>
          {activeTab === "overview" && adviser && (
            <section className={s.detailSection}>
              <div className={s.detailSectionLabel}>Adviser</div>
              <button
                type="button"
                className={`${s.plainBtn} ${s.detailAdviserCard}`}
                onClick={() => onTeacherClick?.(adviser.id)}
                aria-haspopup="dialog"
                aria-label={`View teacher details for ${adviser.name}`}>
                {adviser.photoUrl
                  ? <img src={adviser.photoUrl} alt="" className={s.detailAdviserAvatarImg} />
                  : <div
                      className={s.detailAdviserAvatar}
                      style={{ background: avatarColor(adviser.name) }}>
                      {initials(adviser.name)}
                    </div>
                }
                <div className={s.detailAdviserBody}>
                  <div className={s.detailAdviserName}>{adviser.name}</div>
                  {adviser.role && <div className={s.detailAdviserRole}>{adviser.role}</div>}
                  {adviser.email && (
                    <div className={s.detailAdviserContact}>{adviser.email}</div>
                  )}
                </div>
              </button>
            </section>
          )}

          {activeTab === "overview" && (
          <section className={s.detailSection}>
            <div className={s.detailSectionLabel}>Details</div>
            <dl className={s.detailFacts}>
              <DetailFact label="Grade Level"  value={gradeLevel?.name || "—"} />
              <DetailFact label="Level"        value={level?.name || "—"} />
              <DetailFact
                label="School Year"
                value={schoolYear
                  ? `${schoolYear.label}${schoolYear.active ? " · Active" : ""}`
                  : "—"}
              />
              <DetailFact
                label="Homeroom"
                value={homeroom
                  ? `${homeroom.name}${homeroom.building ? ` · ${homeroom.building}` : ""}`
                  : "—"}
              />
              <DetailFact label="Section ID" value={`#${section.id}`} mono />
            </dl>
          </section>
          )}

          {activeTab === "subjects" && (
          <section className={s.detailSection}>
            <div className={s.detailSectionLabel}>
              Subjects
              {offerings.length > 0 && (
                <span className={s.detailSectionCount}>{offerings.length}</span>
              )}
            </div>
            {offerings.length === 0 ? (
              <div className={s.detailEmpty}>
                No subjects scheduled yet — the registrar hasn&apos;t added class
                offerings for this section.
              </div>
            ) : (
              <ul className={s.detailOfferingList}>
                {offerings.map(({ offering, subject, teacher, semester: rowSem }) => {
                  // Show the "Encode →" deep-link only when the viewer is
                  // both a teacher AND the owner of this specific offering.
                  // The encoder UI / backend re-checks role server-side
                  // (requireTeacherOrAdmin), so this is a UX hint only.
                  const isOwner = isTeacher && myStaffId != null
                    && String(offering.teacherStaffId) === String(myStaffId);
                  return (
                    <li key={offering.id} className={s.detailOffering}>
                      <div className={s.detailOfferingCode}>{subject?.code || "—"}</div>
                      <button
                        type="button"
                        className={`${s.plainBtn} ${s.detailOfferingMain}`}
                        onClick={() => subject && onSubjectClick?.(subject.id)}
                        disabled={!subject}
                        aria-haspopup="dialog"
                        aria-label={subject ? `View subject details for ${subject.title}` : undefined}>
                        <div className={s.detailOfferingTitle}>
                          {subject?.title || "(Untitled subject)"}
                        </div>
                        <div className={s.detailOfferingMeta}>
                          {subject?.units != null && <span>{subject.units} units</span>}
                          {offering.schedule && <span>{offering.schedule}</span>}
                          {offering.room     && <span>Room {offering.room}</span>}
                          {rowSem            && <span>{rowSem.label}</span>}
                        </div>
                      </button>
                      {teacher ? (
                        <button
                          type="button"
                          className={`${s.plainBtn} ${s.detailOfferingTeacher}`}
                          onClick={() => onTeacherClick?.(teacher.id)}
                          aria-haspopup="dialog"
                          aria-label={`View teacher details for ${teacher.name}`}>
                          {teacher.photoUrl
                            ? <img src={teacher.photoUrl} alt="" className={s.teacherAvatarImg} />
                            : <div
                                className={s.teacherAvatar}
                                style={{ background: avatarColor(teacher.name) }}>
                                {initials(teacher.name)}
                              </div>
                          }
                          <div className={s.teacherName}>{teacher.name}</div>
                        </button>
                      ) : (
                        <div className={s.detailOfferingTeacher}>
                          <div className={s.teacherEmpty}>No teacher assigned</div>
                        </div>
                      )}
                      {isOwner && (
                        <Link
                          to={`/teacher/grades/${offering.id}`}
                          className={s.encodeChip}
                          onClick={onClose}
                          title="Open the grade encoder for this class">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2.4"
                               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 20h9"/>
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/>
                          </svg>
                          Encode
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          )}

          {/* Students tab — uses the same SectionRoster component the
              dashboard's modal uses, mounted in default-open mode so the
              grid is visible the moment the tab is shown. Lazy-loaded:
              while students / enrollments are still fetching a small
              placeholder takes the slot so the modal doesn't jump. */}
          {activeTab === "students" && (
          <section className={s.detailSection}>
            <div className={s.detailSectionLabel}>
              Roster
              {rosterCount != null && (
                <span className={s.detailSectionCount}>{rosterCount}</span>
              )}
            </div>
            {roster?.loading ? (
              <div className={s.detailEmpty}>Loading roster…</div>
            ) : roster?.error ? (
              <div className={s.detailEmpty}>{roster.error}</div>
            ) : (
              <SectionRoster
                sectionId={section.id}
                schoolYearId={schoolYear?.id ?? null}
                semesterId={semester?.id ?? null}
                students={roster?.students || []}
                enrollments={roster?.enrollments || []}
                sections={allSections}
                gradeLevels={allGradeLevels}
                schoolYears={allSchoolYears}
                semesters={allSemesters}
                rooms={allRooms}
                defaultOpen
                hideDisclosure
                hideManageButton
              />
            )}
          </section>
          )}

          {/* Grading tab — teacher-only. Each offering the teacher owns in
              this section becomes a prominent card with a big Encode CTA.
              Replaces the "find the encode chip on the subject row" treasure
              hunt with a peer-level, scan-by-card destination. */}
          {activeTab === "grading" && hasGradingTab && (
            <section className={s.detailSection}>
              <div className={s.detailSectionLabel}>
                Your classes here
                <span className={s.detailSectionCount}>{myOfferings.length}</span>
              </div>
              <div className={s.gradingGrid}>
                {myOfferings.map(({ offering, subject, semester: rowSem }, idx) => {
                  const code  = subject?.code || `S${offering.subjectId}`;
                  const abbr  = (code.replace(/[^A-Za-z0-9]/g, "").slice(0, 2) || "S").toUpperCase();
                  return (
                    <article
                      key={offering.id}
                      className={s.gradingCard}
                      style={{ animationDelay: `${Math.min(idx, 6) * 50}ms` }}>
                      <div className={s.gradingCardHead}>
                        <div className={s.gradingCardIcon} aria-hidden="true">{abbr}</div>
                        <div className={s.gradingCardIdent}>
                          <div className={s.gradingCardTitle}
                               title={subject?.title || `Subject #${offering.subjectId}`}>
                            {subject?.title || `Subject #${offering.subjectId}`}
                          </div>
                          <div className={s.gradingCardCode}>{code}</div>
                        </div>
                      </div>
                      <div className={s.gradingCardFacts}>
                        {offering.schedule && (
                          <div className={s.gradingFact}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <circle cx="12" cy="12" r="9"/>
                              <polyline points="12 7 12 12 15 14"/>
                            </svg>
                            <span>{offering.schedule}</span>
                          </div>
                        )}
                        {offering.room && (
                          <div className={s.gradingFact}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"/>
                              <circle cx="12" cy="10" r="3"/>
                            </svg>
                            <span>Room {offering.room}</span>
                          </div>
                        )}
                        {rowSem && (
                          <div className={s.gradingFact}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="3" y="4" width="18" height="18" rx="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8"  y1="2" x2="8"  y2="6"/>
                              <line x1="3"  y1="10" x2="21" y2="10"/>
                            </svg>
                            <span>{rowSem.label} sem</span>
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/teacher/grades/${offering.id}`}
                        className={s.gradingCta}
                        onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.4"
                             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M12 20h9"/>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/>
                        </svg>
                        Open grade encoder
                      </Link>
                    </article>
                  );
                })}
              </div>
              <div className={s.gradingNote}>
                Click any card to jump into the encoder. Your changes save
                locally as you type and notify the student the moment you
                press <strong>Save grades</strong>.
              </div>
            </section>
          )}
        </div>

        <footer className={s.detailFooter}>
          {hasGradingTab && activeTab !== "grading" && (
            <button
              type="button"
              className={s.detailFooterPivot}
              onClick={() => setActiveTab("grading")}>
              <span>
                <strong>You teach {myOfferings.length} class{myOfferings.length === 1 ? "" : "es"} here.</strong>{" "}
                Jump to the Grading tab to encode.
              </span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.4"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          )}
          <button type="button" className={s.detailCloseBtn} onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

/** Tab button used in the section-detail modal's tab strip. Acts as a
 *  `role="tab"` with aria-selected wired so screen readers announce the
 *  active panel. `accent` upgrades the active pill to the gold-on-maroon
 *  variant used for the Grading tab — the destination we most want the
 *  teacher to discover. */
function TabBtn({ id, active, onClick, count, accent, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`${s.tabBtn} ${active ? s.tabBtnActive : ""} ${accent ? s.tabBtnAccent : ""}`}
      onClick={() => onClick(id)}>
      <span className={s.tabBtnLabel}>{children}</span>
      {count != null && count > 0 && (
        <span className={s.tabBtnCount}>{count}</span>
      )}
    </button>
  );
}

/** Single Label / Value row inside the modal's "Details" grid. `mono` opts
 *  into tabular-numeric font for IDs and similar fixed-width values. */
function DetailFact({ label, value, mono }) {
  return (
    <>
      <dt className={s.detailFactLabel}>{label}</dt>
      <dd className={`${s.detailFactValue} ${mono ? s.detailFactMono : ""}`}>{value}</dd>
    </>
  );
}

/** Shimmering placeholder card shown during the initial fetch. Mirrors
 *  the real card silhouette so the layout doesn't shift when data lands.
 *  Pure presentational — no props consumed beyond a styling hook. */
function SkeletonCard({ style }) {
  return (
    <div className={s.skeletonCard} style={style} aria-hidden="true">
      <div className={s.skeletonHeader}>
        <div className={s.skeletonHeaderMain}>
          <div className={`${s.skeletonLine} ${s.skeletonTitle}`} />
          <div className={s.skeletonBadgeRow}>
            <div className={s.skeletonBadge} />
            <div className={s.skeletonBadge} style={{ width: 80 }} />
            <div className={s.skeletonBadge} style={{ width: 70 }} />
          </div>
        </div>
        <div className={s.skeletonChip}>
          <div className={s.skeletonAvatar} />
          <div>
            <div className={`${s.skeletonLine} ${s.skeletonName}`} />
            <div className={`${s.skeletonLine} ${s.skeletonRole}`} />
          </div>
        </div>
      </div>
    </div>
  );
}
